import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { BRAND } from "@/data/site";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-blue-600 text-3xl leading-none">◐</span>
              <span className="font-display font-medium tracking-tight text-2xl text-slate-900">
                Datorra <span className="text-slate-400 font-light">/ India</span>
              </span>
            </Link>
            <p className="mt-6 text-slate-600 text-base leading-relaxed max-w-md">
              Customized business, B2B, B2C, GST and industry databases across India — built for
              teams that treat data like infrastructure, not a spreadsheet.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-slate-700 hover:text-blue-600 transition-colors">
                <Mail className="w-4 h-4" strokeWidth={1.5} /> {BRAND.email}
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-slate-700 hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" strokeWidth={1.5} /> {BRAND.phone}
              </a>
              <a href={`https://wa.me/${BRAND.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-blue-600 transition-colors">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp us
              </a>
              <div className="flex items-start gap-3 text-slate-500 pt-2">
                <MapPin className="w-4 h-4 mt-0.5" strokeWidth={1.5} /> <span>{BRAND.address}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Company</div>
            <ul className="mt-6 space-y-3 text-slate-700 text-sm">
              <li><Link to="/about" className="hover:text-blue-600">About Us</Link></li>
              <li><Link to="/databases" className="hover:text-blue-600">Databases</Link></li>
              <li><Link to="/contact" className="hover:text-blue-600">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Legal</div>
            <ul className="mt-6 space-y-3 text-slate-700 text-sm">
              <li><Link to="/privacy" className="hover:text-blue-600">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-blue-600">Terms & Conditions</Link></li>
              <li><Link to="/refund" className="hover:text-blue-600">Refund Policy</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Start a project</div>
            <p className="mt-6 text-slate-700 text-sm">Need a custom database? Tell us your ICP and we'll ship a sample within 24 hours.</p>
            <Link
              to="/contact"
              data-testid="footer-cta-quote"
              className="group mt-6 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white pl-5 pr-4 py-2.5 text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              Get a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-slate-100">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Popular Searches</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              { l: "B2B Leads Database India", h: "/gst-sales-purchase-data" },
              { l: "Company Database with GST Number", h: "/gst-sales-purchase-data" },
              { l: "GST Sales Purchase Data", h: "/gst-sales-purchase-data" },
              { l: "GST Registered Companies List Download", h: "/gst-sales-purchase-data" },
              { l: "GST Data Provider in India", h: "/gst-sales-purchase-data" },
              { l: "Pan India B2B Database Provider", h: "/gst-sales-purchase-data" },
              { l: "GST Sales Database Providers", h: "/gst-sales-purchase-data" },
              { l: "Verified Company Data Provider India", h: "/gst-sales-purchase-data" },
              { l: "Buy GST Database", h: "/gst-sales-purchase-data" },
              { l: "How to Buy GST Data for Marketing", h: "/gst-sales-purchase-data" },
              { l: "GST Purchase Data Providers", h: "/gst-sales-purchase-data" },
              { l: "Download B2B Sales Leads Data Excel", h: "/gst-sales-purchase-data" },
              { l: "MCA Company Database India", h: "/mca-company-database" },
              { l: "Newly Registered Company Data India", h: "/mca-company-database" },
              { l: "MCA Data Provider India", h: "/mca-company-database" },
              { l: "Director Database India", h: "/mca-company-database" },
              { l: "Pvt Ltd Company Database India", h: "/mca-company-database" },
              { l: "LLP Company Database India", h: "/mca-company-database" },
              { l: "Buy MCA Database", h: "/mca-company-database" },
              { l: "GST Registered Company Database India", h: "/gst-database" },
              { l: "GST Company List India", h: "/gst-database" },
              { l: "Buy GST Database India", h: "/gst-database" },
              { l: "GST Number Database India", h: "/gst-database" },
              { l: "Download GST Registered Company List Excel", h: "/gst-database" },
              { l: "MCA Data by Month", h: "/mca-data" },
              { l: "Buy MCA Company Data", h: "/mca-data" },
              { l: "GST Data by Month", h: "/gst-data" },
              { l: "Buy GST Registered Company Data", h: "/gst-data" },
              { l: "Business Owners Database", h: "/business-owners-database" },
              { l: "Company Directors Database", h: "/company-directors-database" },
              { l: "Business Loan Database", h: "/business-loan-database" },
            ].map((s) => (
              <Link key={s.l} to={s.h} className="inline-flex items-center rounded-full bg-slate-50 border border-slate-100 px-3 py-1.5 text-slate-600 hover:border-blue-600 hover:text-blue-700 transition-colors">
                {s.l}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-slate-400">
          <div>© {year} {BRAND.name}. All rights reserved.</div>
          <div className="uppercase tracking-[0.24em]">Built in India · For India</div>
        </div>
      </div>
    </footer>
  );
}
